import { get } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS, TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import normalizeStringValue from '@commons/utils/normalizeStringValue';

const SHEET_IDS = {
  SUPPLIER_PRODUCT_TRANSFER: 'supplierProductTransfers',
  RECIPE_TRANSFER: 'recipeTransfers',
  STORES: 'stores',
  TYPES: 'types',
  SUPPLIER_PRODUCTS: 'supplierProducts',
  RECIPES: 'recipes',
};

const COLUMN_IDS = {
  ID: 'id',
  NAME: 'name',
  DATE: 'date',
  RECIPIENT_STORE: 'recipientStore',
  RECIPIENT_STORE_ID: 'recipientStoreId',
  SENDER_STORE: 'senderStore',
  SENDER_STORE_ID: 'senderStoreId',
  DELIVERY_TIME: 'deliveryTime',
  TYPE: 'type',
  SUPPLIER_PRODUCT: 'supplierProduct',
  SUPPLIER_PRODUCT_ID: 'supplierProductId',
  SUPPLIER: 'supplier',
  PACKAGING_ID_1: 'packagingId1',
  PACKAGING_NAME_1: 'packagingName1',
  PACKAGING_ID_2: 'packagingId2',
  PACKAGING_NAME_2: 'packagingName2',
  PACKAGING_ID_3: 'packagingId3',
  PACKAGING_NAME_3: 'packagingName3',
  TRANSFER_QUANTITY_1: 'transferQuantity1',
  TRANSFER_QUANTITY_2: 'transferQuantity2',
  TRANSFER_QUANTITY_3: 'transferQuantity3',
  RECIPE: 'recipe',
  RECIPE_ID: 'recipeId',
  RECIPE_UNIT: 'unit',
  CATEGORY: 'category',
  TRANSFER_QUANTITY: 'transferQuantity',
};

const getSheetConfiguration = () => ({
  SUPPLIER_PRODUCT_TRANSFER: {
    id: SHEET_IDS.SUPPLIER_PRODUCT_TRANSFER,
    name: 'SupplierProductTransfers',
  },
  RECIPE_TRANSFER: {
    id: SHEET_IDS.RECIPE_TRANSFER,
    name: 'RecipeTransfers',
  },
  STORES: {
    id: SHEET_IDS.STORES,
    name: 'Stores',
    foreignKeyColumnName: 'name',
  },
  TYPES: {
    id: SHEET_IDS.TYPES,
    name: 'Types',
    foreignKeyColumnName: 'name',
  },
  SUPPLIER_PRODUCTS: {
    id: SHEET_IDS.SUPPLIER_PRODUCTS,
    name: 'SupplierProducts',
    foreignKeyColumnName: 'name',
  },
  RECIPES: {
    id: SHEET_IDS.RECIPES,
    name: 'Recipes',
    foreignKeyColumnName: 'name',
  },
});

const getSPNormalizedColumnIds = () => ({
  [COLUMN_IDS.RECIPIENT_STORE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPIENT_STORE'),
    ),
    key: COLUMN_IDS.RECIPIENT_STORE,
  },
  [COLUMN_IDS.RECIPIENT_STORE_ID]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPIENT_STORE_ID'),
    ),
    key: COLUMN_IDS.RECIPIENT_STORE_ID,
  },
  [COLUMN_IDS.SENDER_STORE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SENDER_STORE'),
    ),
    key: COLUMN_IDS.SENDER_STORE,
  },
  [COLUMN_IDS.SENDER_STORE_ID]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SENDER_STORE_ID'),
    ),
    key: COLUMN_IDS.SENDER_STORE_ID,
  },
  [COLUMN_IDS.DATE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.DATE'),
    ),
    key: COLUMN_IDS.DATE,
    mappingFn: (row, normalizedKey) => {
      const date = get(row, normalizedKey, null);
      const isDateValid = moment(
        date,
        [DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR, DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR],
        true,
      ).isValid();

      // Adding one hour because xlsx parsing remove approximately 30 minutes from a date and there's no real fix for it
      // except this quickfix, see issue: https://github.com/SheetJS/sheetjs/issues/1565
      // We do not take timezone into account so that the timezone stays at DD-MM-YYYY-00-00-00
      return isDateValid
        ? moment(date, [
            DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
            DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR,
          ])
            .utc(true)
            .add(1, 'h')
            .startOf('day')
            .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
        : null;
    },
  },
  [COLUMN_IDS.DELIVERY_TIME]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.DELIVERY_TIME'),
    ),
    key: COLUMN_IDS.DELIVERY_TIME,
    mappingFn: (row, normalizedKey) => {
      const time = get(row, normalizedKey, null);

      const isTimeValid = moment(time, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE, true).isValid();

      if (!isTimeValid) {
        return !time ? null : time;
      }

      return moment(time).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);
    },
  },
  [COLUMN_IDS.TYPE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.TYPE'),
    ),
    key: COLUMN_IDS.TYPE,
  },
  [COLUMN_IDS.SUPPLIER]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SUPPLIER'),
    ),
    key: COLUMN_IDS.SUPPLIER,
  },
  [COLUMN_IDS.SUPPLIER_PRODUCT]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SUPPLIER_PRODUCT'),
    ),
    key: COLUMN_IDS.SUPPLIER_PRODUCT,
  },
  [COLUMN_IDS.SUPPLIER_PRODUCT_ID]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SUPPLIER_PRODUCT_ID'),
    ),
    key: COLUMN_IDS.SUPPLIER_PRODUCT_ID,
  },
  [COLUMN_IDS.PACKAGING_NAME_1]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.PACKAGING_NAME_1'),
    ),
    key: COLUMN_IDS.PACKAGING_NAME_1,
  },
  [COLUMN_IDS.PACKAGING_ID_1]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.PACKAGING_ID_1'),
    ),
    key: COLUMN_IDS.PACKAGING_ID_1,
  },
  [COLUMN_IDS.PACKAGING_NAME_2]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.PACKAGING_NAME_2'),
    ),
    key: COLUMN_IDS.PACKAGING_NAME_2,
  },
  [COLUMN_IDS.PACKAGING_ID_2]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.PACKAGING_ID_2'),
    ),
    key: COLUMN_IDS.PACKAGING_ID_2,
  },
  [COLUMN_IDS.PACKAGING_NAME_3]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.PACKAGING_NAME_3'),
    ),
    key: COLUMN_IDS.PACKAGING_NAME_3,
  },
  [COLUMN_IDS.PACKAGING_ID_3]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.PACKAGING_ID_3'),
    ),
    key: COLUMN_IDS.PACKAGING_ID_3,
  },
  [COLUMN_IDS.TRANSFER_QUANTITY_1]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.TRANSFER_QUANTITY_1'),
    ),
    key: COLUMN_IDS.TRANSFER_QUANTITY_1,
  },
  [COLUMN_IDS.TRANSFER_QUANTITY_2]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.TRANSFER_QUANTITY_2'),
    ),
    key: COLUMN_IDS.TRANSFER_QUANTITY_2,
  },
  [COLUMN_IDS.TRANSFER_QUANTITY_3]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.TRANSFER_QUANTITY_3'),
    ),
    key: COLUMN_IDS.TRANSFER_QUANTITY_3,
  },
});

const getRecipeNormalizedColumnIds = () => ({
  [COLUMN_IDS.RECIPIENT_STORE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPIENT_STORE'),
    ),
    key: COLUMN_IDS.RECIPIENT_STORE,
  },
  [COLUMN_IDS.RECIPIENT_STORE_ID]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPIENT_STORE_ID'),
    ),
    key: COLUMN_IDS.RECIPIENT_STORE_ID,
  },
  [COLUMN_IDS.SENDER_STORE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SENDER_STORE'),
    ),
    key: COLUMN_IDS.SENDER_STORE,
  },
  [COLUMN_IDS.SENDER_STORE_ID]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.SENDER_STORE_ID'),
    ),
    key: COLUMN_IDS.SENDER_STORE_ID,
  },
  [COLUMN_IDS.DATE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.DATE'),
    ),
    key: COLUMN_IDS.DATE,
    mappingFn: (row, normalizedKey) => {
      const date = get(row, normalizedKey, null);
      const isDateValid = moment(
        date,
        [DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR, DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR],
        true,
      ).isValid();

      // Adding one hour because xlsx parsing remove approximately 30 minutes from a date and there's no real fix for it
      // except this quickfix, see issue: https://github.com/SheetJS/sheetjs/issues/1565
      // We do not take timezone into account so that the timezone stays at DD-MM-YYYY-00-00-00
      return isDateValid
        ? moment(date, [
            DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
            DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR,
          ])
            .utc(true)
            .add(1, 'h')
            .startOf('day')
            .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
        : null;
    },
  },
  [COLUMN_IDS.DELIVERY_TIME]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.DELIVERY_TIME'),
    ),
    key: COLUMN_IDS.DELIVERY_TIME,
    mappingFn: (row, normalizedKey) => {
      const time = get(row, normalizedKey, null);

      const isTimeValid = moment(time, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE, true).isValid();

      if (!isTimeValid) {
        return !time ? null : time;
      }

      return moment(time).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);
    },
  },
  [COLUMN_IDS.TYPE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.TYPE'),
    ),
    key: COLUMN_IDS.TYPE,
  },
  [COLUMN_IDS.CATEGORY]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.CATEGORY'),
    ),
    key: COLUMN_IDS.CATEGORY,
  },
  [COLUMN_IDS.RECIPE]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPE'),
    ),
    key: COLUMN_IDS.RECIPE,
  },
  [COLUMN_IDS.RECIPE_ID]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPE_ID'),
    ),
    key: COLUMN_IDS.RECIPE_ID,
  },
  [COLUMN_IDS.RECIPE_UNIT]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.RECIPE_UNIT'),
    ),
    key: COLUMN_IDS.RECIPE_UNIT,
  },
  [COLUMN_IDS.TRANSFER_QUANTITY]: {
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSFERS.BATCH_CREATION_TEMPLATE.TRANSFER_QUANTITY'),
    ),
    key: COLUMN_IDS.TRANSFER_QUANTITY,
  },
});

export default {
  getSheetConfiguration,
  getSPNormalizedColumnIds,
  getRecipeNormalizedColumnIds,
};
